import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Tooltip,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { UsageDataPoint } from "../types";
import { InfoTooltip } from "./InfoTooltip";

interface UsageChartProps {
  data: UsageDataPoint[];
  maxValue: number;
}

export const UsageChart: React.FC<UsageChartProps> = ({ data, maxValue }) => {
  const theme = useTheme();
  const safeMax = maxValue || 1;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Report Generation Over Time
            </Typography>
            <InfoTooltip title="Stacked bar chart showing completed and failed report generation jobs per time period. Grouped by day, week, or month based on filter selection." />
          </Box>
        }
        subheader="Completed and failed reports per period"
        action={
          <Stack direction="row" spacing={2} sx={{ mr: 1 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: "primary.main" }} />
              <Typography variant="caption" color="text.secondary">Completed</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: "error.main" }} />
              <Typography variant="caption" color="text.secondary">Failed</Typography>
            </Stack>
          </Stack>
        }
      />
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: { xs: 0.5, sm: 1 },
            height: 220,
            pt: 2,
          }}
        >
          {data.map((d) => {
            const total = d.completed + d.failed;
            const completedPct = (d.completed / safeMax) * 100;
            const failedPct = (d.failed / safeMax) * 100;

            return (
              <Tooltip
                key={d.label}
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={600}>{d.label}</Typography>
                    <br />
                    <Typography variant="caption">{d.completed} completed</Typography>
                    {d.failed > 0 && (
                      <>
                        <br />
                        <Typography variant="caption" color="error.light">
                          {d.failed} failed
                        </Typography>
                      </>
                    )}
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 48,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "stretch",
                      justifyContent: "flex-end",
                      height: "100%",
                    }}
                  >
                    {/* Failed segment (on top) */}
                    {d.failed > 0 && (
                      <Box
                        sx={{
                          height: `${failedPct}%`,
                          minHeight: 4,
                          borderRadius: total === d.failed ? "12px 12px 6px 6px" : "12px 12px 0 0",
                          bgcolor: "error.main",
                          transition: "all 0.2s ease-out",
                        }}
                      />
                    )}
                    {/* Completed segment */}
                    {d.completed > 0 && (
                      <Box
                        sx={{
                          height: `${completedPct}%`,
                          minHeight: 8,
                          borderRadius: d.failed > 0 ? "0 0 6px 6px" : "12px 12px 6px 6px",
                          background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "center",
                          pt: completedPct > 15 ? 0.5 : 0,
                          transition: "all 0.2s ease-out",
                          boxShadow: 1,
                          "&:hover": {
                            transform: "translateY(-4px)",
                            boxShadow: 3,
                          },
                        }}
                      >
                        {completedPct > 15 && (
                          <Typography variant="caption" sx={{ color: "#fff", fontWeight: 600, fontSize: 10 }}>
                            {d.completed}
                          </Typography>
                        )}
                      </Box>
                    )}
                    {total === 0 && (
                      <Box
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.grey[400], 0.2),
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, fontWeight: 500, fontSize: 10 }}
                  >
                    {d.label.slice(5) || d.label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};
